import { useEffect, useRef, useState } from 'react'
import { itemsService } from '../services/api'
import { resolveCardImage } from '../utils/itemMeta'
import { nextImageLoadAttempt } from '../utils/itemQueryPolicy'

export function useProductCardImage(item) {
  const resolved = resolveCardImage(item)
  const [src, setSrc] = useState(resolved.url)
  const [failed, setFailed] = useState(false)
  const retriesRef = useRef(0)
  const identity = `${item?.id || ''}|${resolved.url}|${resolved.key}`

  useEffect(() => {
    retriesRef.current = 0
    setFailed(false)
    setSrc(resolved.url)
  }, [identity, resolved.url])

  useEffect(() => {
    if (resolved.url || !resolved.key || failed) return undefined
    let cancelled = false
    itemsService.signedMedia(resolved.key).then((url) => {
      if (cancelled) return
      if (url) setSrc(url)
      else setFailed(true)
    }).catch(() => {
      if (!cancelled) setFailed(true)
    })
    return () => {
      cancelled = true
    }
  }, [identity, resolved.url, resolved.key, failed])

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState !== 'visible') return
      if (!failed || !resolved.key) return
      retriesRef.current = 0
      setFailed(false)
    }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('focus', resume)
    return () => {
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('focus', resume)
    }
  }, [failed, resolved.key])

  const onError = () => {
    const attempt = nextImageLoadAttempt(retriesRef.current, Boolean(resolved.key))
    if (attempt !== 'resign') {
      setFailed(true)
      return
    }
    retriesRef.current += 1
    itemsService.signedMedia(resolved.key).then((url) => {
      if (url && url !== src) setSrc(url)
      else setFailed(true)
    }).catch(() => setFailed(true))
  }

  return { src: failed ? '' : src, onError }
}
